import {useCallback, useRef, useState} from 'react';

interface UseInfinitePaginationOptions {
    hasMore: boolean;
    fetchPage: (page: number) => Promise<unknown>;
    initialPage?: number;
}

interface UseInfinitePaginationResult {
    currentPage: number;
    isLoadingInitial: boolean;
    isLoadingMore: boolean;
    loadInitial: () => Promise<void>;
    loadNext: () => Promise<void>;
    reset: () => void;
}

export function useInfinitePagination({
    hasMore,
    fetchPage,
    initialPage = 0,
}: UseInfinitePaginationOptions): UseInfinitePaginationResult {
    const [currentPage, setCurrentPage] = useState<number>(initialPage);
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const initialLoadingRef = useRef(false);
    const loadingMoreRef = useRef(false);

    const loadInitial = useCallback(async () => {
        if (initialLoadingRef.current) {
            return;
        }

        initialLoadingRef.current = true;
        setCurrentPage(initialPage);
        setIsLoadingMore(false);
        setIsLoadingInitial(true);

        try {
            await fetchPage(initialPage);
        } catch {
            // errors are handled by the caller
        }
        finally {
            setIsLoadingInitial(false);
            initialLoadingRef.current = false;
        }
    }, [fetchPage, initialPage]);

    const loadNext = useCallback(async () => {
        if (loadingMoreRef.current || isLoadingMore || !hasMore) {
            return;
        }

        const nextPage = currentPage + 1;
        loadingMoreRef.current = true;
        setIsLoadingMore(true);
        try {
            await fetchPage(nextPage);
            setCurrentPage(nextPage);
        } catch {
            // errors are handled by the caller
        }
        finally {
            setIsLoadingMore(false);
            loadingMoreRef.current = false;
        }
    }, [currentPage, fetchPage, hasMore, isLoadingMore]);

    const reset = useCallback(() => {
        setCurrentPage(initialPage);
        setIsLoadingMore(false);
        initialLoadingRef.current = false;
        loadingMoreRef.current = false;
    }, [initialPage]);

    return {
        currentPage,
        isLoadingInitial,
        isLoadingMore,
        loadInitial,
        loadNext,
        reset,
    };
}
